import { JSX, useContext, useMemo } from "react";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { BenchmarkingSpecialtyGroup } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { BenchmarkingCounty, BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { QHPFiltersContext } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { MedicareFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { Accordion, AccordionContainer } from "@app/shared";
import { DropdownItem } from "@app/shared/checkbox-dropdown";

const BenchmarkingFiltersOverview = (): JSX.Element => {
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMarketplaceFiltersContext = useContext(QHPFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);

  const accordionTitle: string | JSX.Element = useMemo(() => {
    let totalFilters = 0;

    if (!!benchmarkingFiltersContext.displayedPrimaryFilters && !!networkComparisonFiltersContext.displayedNetworkComparisonFilters) {
      totalFilters = (!!benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId ? 1 : 0) +
        benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.length +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks.length +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.volumeIndicators.length +
        (!!networkComparisonFiltersContext.displayedNetworkComparisonFilters.pcaLineOfBusiness ? 1 : 0) +
        (!!networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork ? 1 : 0) +
        (!!networkComparisonFiltersContext.displayedNetworkComparisonFilters.specialtyGroupId ? 1 : 0);
    }

    return `Filters: ${totalFilters} Applied`;
  }, [
    benchmarkingFiltersContext.displayedPrimaryFilters,
    networkComparisonFiltersContext.displayedNetworkComparisonFilters,
  ]);

  const displayedFilters = useMemo(() => {
    if (!!benchmarkingFiltersContext.displayedPrimaryFilters && !!networkComparisonFiltersContext.displayedNetworkComparisonFilters) {
      const getCountyName = (countyId: number): string => {
        const getLineOfBusinessContext = () => {
          switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
            case BenchmarkingLineOfBusinessEnum.Marketplace:
              return benchmarkingMarketplaceFiltersContext;
            case BenchmarkingLineOfBusinessEnum.Medicare:
              return benchmarkingMedicareFiltersContext;
            default:
              return;
          }
        }

        const county: DropdownItem<BenchmarkingCounty> = getLineOfBusinessContext()?.counties.find(x => x.key.countyId === countyId);

        if (!!county) {
          return county.key.countyName;
        }
      }

      const lineOfBusiness: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {BenchmarkingLineOfBusinessEnum[benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId]}
        </span>
      );

      const counties: JSX.Element[] = benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.map((county) => (
        <span key={county} className="dashboard-filters-overview__filter">
          {getCountyName(county)}
        </span>
      ));

      const hContracts: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks.map((hContract, index) => (
        <span key={index} className="dashboard-filters-overview__filter">
          {hContract.contractId}
        </span>
      ));

      const volumeIndicators: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.volumeIndicators.map((volumeIndicator) => (
        <span key={volumeIndicator} className="dashboard-filters-overview__filter">
          {volumeIndicator}
        </span>
      ));

      const pcaLineOfBusiness: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {networkComparisonFiltersContext.displayedNetworkComparisonFilters.pcaLineOfBusiness}
        </span>
      );

      const primaryNetwork: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork?.contractId}
        </span>
      );

      const getSpecialtyName = (specialtyGroupId: number): string => {
        const specialty: BenchmarkingSpecialtyGroup = benchmarkingFiltersContext.specialties.find(x => x.specialtyGroupId === specialtyGroupId);

        if (!!specialty) {
          return `${specialtyGroupId !== 0 ? `${specialty.specialtyGroupCode} - ` : ""}${specialty.specialtyGroupName}`;
        }
      }

      const specialty: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {getSpecialtyName(networkComparisonFiltersContext.displayedNetworkComparisonFilters.specialtyGroupId)}
        </span>
      );

      return {
        counties,
        hContracts,
        lineOfBusiness,
        pcaLineOfBusiness,
        primaryNetwork,
        specialty,
        volumeIndicators,
      };
    }
  }, [
    benchmarkingFiltersContext.displayedPrimaryFilters,
    networkComparisonFiltersContext.displayedNetworkComparisonFilters,
  ]);

  return (
    <div className="row">
      <div className="col-12">
        <AccordionContainer>
          <Accordion
            testId="benchmarking-dashboard__filters-applied--accordion-panel"
            title={accordionTitle}
          >
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Line Of Business:</span>
              <span>{displayedFilters?.lineOfBusiness}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Counties:</span>
              <span>{displayedFilters?.counties}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Networks:</span>
              <span>{displayedFilters?.hContracts}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Volume Indicators:</span>
              <span>{displayedFilters?.volumeIndicators}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">PCA Line of Business:</span>
              <span>{displayedFilters?.pcaLineOfBusiness}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Primary Network:</span>
              <span>{displayedFilters?.primaryNetwork}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Specialty:</span>
              <span>{displayedFilters?.specialty}</span>
            </div>
          </Accordion>
        </AccordionContainer>
      </div>
    </div>
  );
}

export default BenchmarkingFiltersOverview;